/**
 * Hostname-aware URLs.
 *
 * Same Vite bundle, two production hostnames:
 *   - engage.paybyrd.com   — admin SPA (with /portal/* tucked alongside)
 *   - partners.paybyrd.com — partner portal at the ROOT path
 *
 * The SPA branches on hostname at startup: the admin route tree is
 * suppressed on partners.* and the portal becomes the root. Cross-links
 * between the two surfaces have to be absolute URLs across hosts.
 *
 * In dev / preview / Railway-direct URLs (no `partners.` prefix), we
 * fall back to path-based routing (`/portal/*`) so the same code works
 * locally without any env-var ceremony.
 */

const PARTNER_HOST_PREFIX = "partners.";

/** True when this tab is loaded on the dedicated partner subdomain. */
export function isPartnerHost(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.hostname.startsWith(PARTNER_HOST_PREFIX);
}

/** Absolute URL of the partner portal home, branching on whether we
 *  have a sibling `partners.*` host or are operating in path-prefix
 *  mode (dev, Railway-direct).
 *
 *  Examples:
 *    engage.paybyrd.com        → https://partners.paybyrd.com/dashboard
 *    paybyrd-engage-frontend-production.up.railway.app
 *                              → /portal/dashboard (same-origin, path)
 *    localhost:5173            → /portal/dashboard (same-origin, path)
 */
export function partnerPortalUrl(path: string = "/dashboard"): string {
  if (typeof window === "undefined") return `/portal${path}`;
  const host = window.location.hostname;
  // Already on partners.* — same-origin link.
  if (host.startsWith(PARTNER_HOST_PREFIX)) return path;
  // engage.paybyrd.com → partners.paybyrd.com (drop "engage.", prepend "partners.")
  if (host.startsWith("engage.")) {
    const rest = host.substring("engage.".length);
    return `${window.location.protocol}//${PARTNER_HOST_PREFIX}${rest}${path}`;
  }
  // Dev / Railway-direct — keep as path-based.
  return `/portal${path}`;
}

/** Absolute URL of the admin SPA home from anywhere.
 *
 *  Examples:
 *    partners.paybyrd.com → https://engage.paybyrd.com/
 *    engage.paybyrd.com   → /  (already there)
 *    localhost:5173       → /  (path-based)
 */
export function adminPortalUrl(path: string = "/"): string {
  if (typeof window === "undefined") return path;
  const host = window.location.hostname;
  if (host.startsWith(PARTNER_HOST_PREFIX)) {
    const rest = host.substring(PARTNER_HOST_PREFIX.length);
    return `${window.location.protocol}//engage.${rest}${path}`;
  }
  // Already on engage.* / dev / Railway-direct — same-origin link.
  return path;
}
